import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination } from '@mui/material'
import api from 'utils/api'
import { useAdminData, timestamp } from './shared'
import SearchField from './SearchField'

const PAGE_SIZE = 20
const codeStatus = code => !code.enabled ? 'Paused' : code.claims >= code.claimLimit ? 'Used up' : 'Active'

const validLimit = value => value !== '' && Number.isSafeInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 100000000

function Configuration({ config, refresh }) {
  const [draft, setDraft] = useState(null)
  const current = draft || config
  const mode = current.mode
  const limit = String(current.limit)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const save = async () => {
    setBusy(true)
    setMessage(null)
    try {
      await api.put(`/admin/admission/${config.accountType}`, { mode, limit: mode === 'INVITE' ? Number(limit) : config.limit, revision: current.revision })
      setMessage({ severity: 'success', text: 'Registration settings saved.' })
      await refresh()
      setDraft(null)
    } catch (error) { setMessage({ severity: 'error', text: error.message || 'Settings could not be saved.' }) }
    finally { setBusy(false) }
  }
  return <Paper variant="outlined" sx={{ p: 2.5 }}><Stack spacing={2}>
    <Typography fontWeight={600}>{config.accountType === 'PERSONAL' ? 'Personal accounts' : 'Business accounts'}</Typography>
    {mode === 'INVITE' && <>
      <Typography>{config.admitted.toLocaleString()} admitted · {Math.max(0, config.limit - config.admitted).toLocaleString()} overall places remaining</Typography>
      <Typography variant="body2" color="text.secondary">Totals include open registration and exclude accounts created before tracking began.</Typography>
    </>}
    <TextField select size="small" label="Registration mode" value={mode} disabled={busy} onChange={e => setDraft({ ...current, mode: e.target.value })}>
      <MenuItem value="OPEN">Open registration</MenuItem><MenuItem value="INVITE">Invite code required</MenuItem><MenuItem value="PAUSED">Registration paused</MenuItem>
    </TextField>
    {mode === 'OPEN' && <Alert severity="info">Anyone can register without a code. Invitation limits are not enforced in this mode.</Alert>}
    {mode === 'INVITE' && <Alert severity="info">New accounts require an enabled code with remaining claims and an available overall place. Without a valid code, registration is blocked.</Alert>}
    {mode === 'PAUSED' && <Alert severity="info">New registrations{config.accountType === 'BUSINESS' ? ' and business upgrades' : ''} are paused. Existing users can still log in.</Alert>}
    {mode === 'INVITE' && <TextField size="small" label="Overall admission limit" type="number" value={limit} disabled={busy} onChange={e => setDraft({ ...current, limit: e.target.value })} inputProps={{ min: 0, max: 100000000, step: 1 }} />}
    {message && <Alert severity={message.severity}>{message.text}</Alert>}
    <Button variant="contained" onClick={save} disabled={busy || (mode === 'INVITE' && !validLimit(limit))}>{busy ? 'Saving…' : 'Save settings'}</Button>
    {draft && <Button disabled={busy} onClick={() => setDraft(null)}>Discard registration edits</Button>}
  </Stack></Paper>
}

function CreateCode({ refresh, onClose, onSuccess }) {
  const [code, setCode] = useState('')
  const [limit, setLimit] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const create = async () => {
    setBusy(true)
    setMessage(null)
    try {
      await api.post('/admin/invitations', { code: code.trim(), claimLimit: Number(limit) })
      await refresh()
      onSuccess()
    } catch (error) { setMessage({ severity: 'error', text: error.message || 'Code could not be created.' }) }
    finally { setBusy(false) }
  }
  return <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}><DialogTitle>Create invite code</DialogTitle><DialogContent><Stack spacing={2.5} pt={1}>
    <Typography variant="body2">Create independently managed invite codes. Existing codes stay valid until their claim limit is reached or you disable them.</Typography>
    <TextField label="New invite code" value={code} onChange={e => setCode(e.target.value)} autoComplete="off" inputProps={{ maxLength: 64 }}
      helperText="Use 8–64 letters, numbers, hyphens, or underscores. Admins can view and copy the code after saving." />
    <TextField label="New code claim limit" type="number" value={limit} onChange={e => setLimit(e.target.value)} inputProps={{ min: 0, max: 100000000, step: 1 }} />
    {message && <Alert severity={message.severity}>{message.text}</Alert>}
  </Stack></DialogContent><DialogActions>
    <Button onClick={onClose} disabled={busy}>Cancel</Button>
    <Button variant="contained" onClick={create} disabled={busy || !/^[A-Za-z0-9_-]{8,64}$/.test(code.trim()) || !validLimit(limit)}>
      {busy ? 'Creating…' : 'Create code'}</Button>
  </DialogActions></Dialog>
}

function CodeSettings({ code, refresh }) {
  const name = `Invite code #${code.invitationId}`
  const [draft, setDraft] = useState(null)
  const [copyMessage, setCopyMessage] = useState(null)
  const current = draft || code
  const limit = String(current.claimLimit)
  const enabled = current.enabled
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const save = async (nextEnabled = enabled) => {
    setBusy(true)
    setMessage(null)
    try {
      await api.put(`/admin/invitations/${code.invitationId}`, { claimLimit: Number(limit), enabled: nextEnabled, revision: current.revision })
      setMessage({ severity: 'success', text: 'Code settings saved.' })
      await refresh()
      setDraft(null)
    } catch (error) { setMessage({ severity: 'error', text: error.message || 'Code settings could not be saved.' }) }
    finally { setBusy(false) }
  }
  return <TableRow hover>
    <TableCell>
      <Typography variant="body2">{name}</Typography>
      {code.code ? <>
        <Typography component="code" aria-label={`Code value for ${name}`} sx={{ display: 'block', my: 0.5, overflowWrap: 'anywhere' }}>{code.code}</Typography>
        <Button size="small" onClick={async () => {
          try { await navigator.clipboard.writeText(code.code); setCopyMessage('Invite code copied.') }
          catch (_) { setCopyMessage('Copy failed. Select and copy the code above.') }
        }}>Copy code</Button>
      </> : <Typography variant="body2">Original code unavailable</Typography>}
      {copyMessage && <Typography variant="body2">{copyMessage}</Typography>}
    </TableCell>
    <TableCell>{codeStatus(code)}</TableCell>
    <TableCell>{code.claims} used · {Math.max(0, code.claimLimit - code.claims)} remaining<br />
      {code.personalClaims} personal · {code.businessClaims} business</TableCell>
    <TableCell><TextField size="small" label={`Claim limit for ${name}`} type="number" value={limit} disabled={busy}
      onChange={e => setDraft({ ...current, claimLimit: e.target.value })} inputProps={{ min: 0, max: 100000000, step: 1 }}
      helperText="Total claims allowed" sx={{ minWidth: 160 }} /></TableCell>
    <TableCell>{timestamp(code.createdAt)}</TableCell>
    <TableCell><Stack spacing={1}>
      {code.inviteToken && <Button size="small" onClick={async () => {
        const link = `${window.location.origin}/signup?invite=${encodeURIComponent(code.inviteToken)}`
        try { await navigator.clipboard.writeText(link); setCopyMessage('Invitation link copied.') }
        catch (_) { setCopyMessage('Copy failed. Please try again.') }
      }}>Copy link</Button>}
      <Button size="small" onClick={() => save()} disabled={busy || !validLimit(limit)} aria-label={`Save ${name}`}>{busy ? 'Saving…' : 'Save limit'}</Button>
      <Button size="small" color={code.enabled ? 'warning' : 'primary'} onClick={() => save(!code.enabled)} disabled={busy || !validLimit(limit)}
        aria-label={`${code.enabled ? 'Pause' : 'Resume'} ${name}`}>{code.enabled ? 'Pause' : 'Resume'}</Button>
      {draft && <Button disabled={busy} onClick={() => setDraft(null)}>Discard edits for {name}</Button>}
      {message && <Alert severity={message.severity}>{message.text}</Alert>}
    </Stack></TableCell>
  </TableRow>
}

function InviteCodes({ codes, refresh }) {
  const [filters, setFilters] = useState({ q: '', status: '' })
  const [page, setPage] = useState(1)
  const change = (key, value) => { setFilters(current => ({ ...current, [key]: value })); setPage(1) }
  const matching = (codes.data || []).filter(code =>
    (!filters.status || codeStatus(code) === filters.status) &&
    (!filters.q.trim() || (code.code || '').toLowerCase().includes(filters.q.trim().toLowerCase()) || String(code.invitationId) === filters.q.trim()))
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)) || b.invitationId - a.invitationId)
  const pageCount = Math.max(1, Math.ceil(matching.length / PAGE_SIZE))
  const activePage = Math.min(page, pageCount)
  useEffect(() => { if (page > pageCount) setPage(pageCount) }, [page, pageCount])
  return <Stack spacing={2.5}>
    <Paper variant="outlined" sx={{ p: 2.5 }}><Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
      <SearchField label="Search invite code or ID" value={filters.q} onChange={value => change('q', value)} />
      <TextField select size="small" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} label="Code status" value={filters.status} onChange={event => change('status', event.target.value)} sx={{ minWidth: 150 }}>
        <MenuItem value="">All statuses</MenuItem>{['Active', 'Paused', 'Used up'].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
      </TextField>
      <Button onClick={() => { setFilters({ q: '', status: '' }); setPage(1) }}>Clear code filters</Button>
    </Stack></Paper>
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Typography fontWeight={600} p={2}>Invite codes {codes.data && `(${matching.length})`}</Typography>
      {codes.error && <Alert severity="error" sx={{ m: 2 }}>{codes.error.message}</Alert>}
      {!codes.data && !codes.error && <Typography p={2}>Loading codes…</Typography>}
      {codes.data && <>
        <TableContainer><Table size="small" sx={{ minWidth: 1000, '& th': { bgcolor: 'grey.50', color: 'text.secondary' } }}>
          <TableHead><TableRow>{['Invite code', 'Status', 'Usage by account type', 'Claim limit', 'Created', 'Actions'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>{matching.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE).map(code => <CodeSettings key={code.invitationId} code={code} refresh={refresh} />)}
            {!matching.length && <TableRow><TableCell colSpan={6} align="center"><Box py={3}>No invite codes match these filters.</Box></TableCell></TableRow>}
          </TableBody>
        </Table></TableContainer>
        {pageCount > 1 && <Box display="flex" justifyContent="flex-end" p={2}><Pagination aria-label="Invite codes pagination" count={pageCount} page={activePage} onChange={(_, value) => setPage(value)} /></Box>}
      </>}
    </Paper>
  </Stack>
}

export default function Admissions() {
  const configs = useAdminData('/admin/admission')
  const [creating, setCreating] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [filters, setFilters] = useState({ accountType: '', kind: '', emailVerified: '', q: '' })
  const [page, setPage] = useState(0)
  const recordQuery = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE), ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '')) })
  const records = useAdminData(`/admin/admission/records?${recordQuery}`)
  const codes = useAdminData('/admin/invitations')
  const changeFilter = (key, value) => { setFilters(current => ({ ...current, [key]: value })); setPage(0) }
  const refresh = async () => { await Promise.all([configs.mutate(), records.mutate(), codes.mutate()]) }
  useEffect(() => {
    const refreshVisible = () => {
      if (document.visibilityState === 'visible' && navigator.onLine !== false) {
        Promise.allSettled([configs.mutate(), records.mutate(), codes.mutate()])
      }
    }
    const timer = window.setInterval(refreshVisible, 10000)
    document.addEventListener('visibilitychange', refreshVisible)
    window.addEventListener('focus', refreshVisible)
    window.addEventListener('online', refreshVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', refreshVisible)
      window.removeEventListener('focus', refreshVisible)
      window.removeEventListener('online', refreshVisible)
    }
  }, [configs.mutate, records.mutate, codes.mutate])
  if (configs.error && !configs.data) return <Alert severity="error" action={<Button onClick={() => configs.mutate()}>Retry</Button>}>{configs.error.message}</Alert>
  if (!configs.data) return <Typography>Loading invitations…</Typography>
  return <Stack spacing={2.5}>
    {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.message}</Alert>}
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2}>
      <Box><Typography color="text.secondary">Manage registration access, invitation codes and admission records.</Typography>
        <Typography variant="caption" color="text.secondary">Usage refreshes every 10 seconds while this page is visible.</Typography></Box>
      <Stack direction="row" gap={1}><Button onClick={() => refresh().catch(error => setFeedback({ severity: 'error', message: error.message || 'Refresh failed. Try again.' }))}>Refresh</Button><Button variant="contained" onClick={() => setCreating(true)}>Create invite code</Button></Stack>
    </Stack>
    {configs.error && <Alert severity="error">{configs.error.message}</Alert>}
    <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
      {configs.data.map(config => <Configuration key={config.accountType} config={config} refresh={refresh} />)}
    </Box>
    {creating && <CreateCode refresh={refresh} onClose={() => setCreating(false)} onSuccess={() => {
      setCreating(false); setFeedback({ severity: 'success', message: 'Invite code created. Other codes remain unchanged.' })
    }} />}
    <InviteCodes codes={codes} refresh={refresh} />
    <Paper variant="outlined" sx={{ p: 2.5 }}><Stack direction={{ xs: 'column', md: 'row' }} gap={2} flexWrap="wrap">
      <SearchField label="Search email, user ID or invite code ID" value={filters.q} onChange={value => changeFilter('q', value)} />
      <TextField select size="small" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} label="Record account type" value={filters.accountType} onChange={event => changeFilter('accountType', event.target.value)} sx={{ minWidth: 170 }}>
        <MenuItem value="">All accounts</MenuItem><MenuItem value="PERSONAL">Personal</MenuItem><MenuItem value="BUSINESS">Business</MenuItem>
      </TextField>
      <TextField select size="small" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} label="Admission type" value={filters.kind} onChange={event => changeFilter('kind', event.target.value)} sx={{ minWidth: 170 }}>
        <MenuItem value="">All admissions</MenuItem><MenuItem value="SIGNUP">Registration</MenuItem><MenuItem value="UPGRADE">Business upgrade</MenuItem>
      </TextField>
      <TextField select size="small" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} label="Email verification" value={filters.emailVerified} onChange={event => changeFilter('emailVerified', event.target.value)} sx={{ minWidth: 170 }}>
        <MenuItem value="">All</MenuItem><MenuItem value="true">Verified</MenuItem><MenuItem value="false">Unverified</MenuItem>
      </TextField>
      <Button onClick={() => { setFilters({ accountType: '', kind: '', emailVerified: '', q: '' }); setPage(0) }}>Clear filters</Button>
    </Stack></Paper>
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Typography fontWeight={600} p={2}>Admission records {records.data && `(${records.data.total})`}</Typography>
      {records.error && <Alert severity="error" sx={{ m: 2 }}>{records.error.message}</Alert>}
      {!records.data && !records.error && <Typography p={2}>Loading records…</Typography>}
      {records.data && <>
        <TableContainer><Table size="small" sx={{ minWidth: 700 }}><TableHead><TableRow>{['User', 'Email', 'Account type', 'Admission', 'Invite code', 'Email verified', 'Date'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>{records.data.items.map(row => <TableRow key={row.id} hover>
            <TableCell>{row.userId}</TableCell><TableCell>{row.email || 'Account unavailable'}</TableCell><TableCell>{row.accountType === 'BUSINESS' ? 'Business' : 'Personal'}</TableCell>
            <TableCell>{row.kind === 'UPGRADE' ? 'Business upgrade' : 'Registration'}</TableCell><TableCell>{row.invitationId == null ? row.codeId == null ? 'No invite code' : `Legacy invite code #${row.codeId}` : `Invite code #${row.invitationId}`}</TableCell>
            <TableCell>{row.emailVerified == null ? 'Unavailable' : row.emailVerified ? 'Yes' : 'No'}</TableCell><TableCell>{timestamp(row.createdAt)}</TableCell>
          </TableRow>)}
            {!records.data.items.length && <TableRow><TableCell colSpan={7} align="center">No admissions match these filters.</TableCell></TableRow>}
          </TableBody></Table></TableContainer>
        {records.data.total > PAGE_SIZE && <Box display="flex" justifyContent="flex-end" p={2}>
          <Pagination aria-label="Admission records pagination" count={Math.ceil(records.data.total / PAGE_SIZE)} page={page + 1} onChange={(_, value) => setPage(value - 1)} />
        </Box>}
      </>}
    </Paper>
  </Stack>
}
